/**
 * Backlog Parser Utility
 * 
 * Parses markdown backlog output from the AI into structured data.
 * Handles various formats including tables and extracts user stories, epics, and metadata.
 */

import { 
  UserStory, 
  Epic, 
  ParsedBacklog, 
  StandardTask,
  STANDARD_TASKS,
  getDefaultTechnicalCriteriaForStory,
  parsePriorityAndSequence,
  formatPriorityTag,
  normalizePrioritySequence
} from '../types/story';
import { buildHuId, normalizeStoryId } from './storyIds';

/**
 * Generate a unique ID
 */
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const resolveStoryId = (value: string | null | undefined, fallbackIndex: number): string => {
  const normalized = normalizeStoryId(value);
  if (normalized && /^HU-\d+$/i.test(normalized)) {
    return normalized;
  }
  return buildHuId(fallbackIndex + 1);
};

const STORY_HEADER_LINE_REGEX = /^\s*(?:#{2,3}\s*)?(?:\*\*)?HU-\d+(?:\*\*)?\s*(?:[:-]\s*|$)/i;
const STORY_HEADER_CAPTURE_REGEX = /^\s*(?:#{2,3}\s*)?(?:\*\*)?(HU-\d+)(?:\*\*)?\s*(?:[:-]\s*|$)\s*(.*)$/i;

const sectionHasStoryHeader = (section: string): boolean => {
  const firstLines = section
    .split(/[\r\n]+/)
    .slice(0, 8)
    .join('\n');
  return STORY_HEADER_LINE_REGEX.test(firstLines);
};

/**
 * Parse story points from text
 */
const parseStoryPoints = (text: string): number => {
  // Look for fibonacci numbers
  const match = text.match(/(\d+)\s*(pts?|puntos?|points?|sp)?/i);
  if (match) {
    const num = parseInt(match[1]);
    // Validate fibonacci sequence
    if ([1, 2, 3, 5, 8, 13].includes(num)) {
      return num;
    }
    // Map to closest fibonacci
    if (num <= 1) return 1;
    if (num <= 2) return 2;
    if (num <= 4) return 3;
    if (num <= 6) return 5;
    if (num <= 10) return 8;
    return 13;
  }
  
  // Check for T-shirt sizes
  const lower = text.toLowerCase();
  if (lower.includes('xs') || lower.includes('extra small')) return 1;
  if (lower.includes('small') || lower === 's') return 2;
  if (lower.includes('medium') || lower === 'm') return 3;
  if (lower.includes('large') || lower === 'l') return 5;
  if (lower.includes('xl') || lower.includes('extra large')) return 8;
  if (lower.includes('xxl')) return 13;
  
  return 3; // Default
};

/**
 * Extract role from user story text or title
 */
const extractRole = (text: string, title?: string): string => {
  // First check for [Tag] in title
  if (title) {
    const tagMatch = title.match(/\[(Back|Front|Integración|Integracion|Funcional|Negocio|QA|QA-Auto|QA-NF|Arquitectura|Data|UX|DevOps|Seguridad|Mobile|Docs)\]/i);
    if (tagMatch) {
      const roleMap: Record<string, string> = {
        'back': 'Desarrollador Backend',
        'front': 'Desarrollador Frontend',
        'integración': 'Equipo Integración',
        'integracion': 'Equipo Integración',
        'funcional': 'Analista Funcional',
        'negocio': 'Analista de Negocio',
        'qa': 'QA Funcional',
        'qa-auto': 'Automatizador QA',
        'qa-nf': 'QA No Funcional',
        'arquitectura': 'Arquitecto Software',
        'data': 'Ingeniero de Datos',
        'ux': 'Diseñador UX/UI',
        'devops': 'Ingeniero DevOps',
        'seguridad': 'Ingeniero Seguridad',
        'mobile': 'Desarrollador Mobile',
        'docs': 'Documentador Técnico'
      };
      return roleMap[tagMatch[1].toLowerCase()] || tagMatch[1];
    }
  }
  
  // Then check for "Como [rol]" in user story
  const match = text.match(/(?:yo\s+)?como\s+(?:un|una|el|la)?\s*([^,]+)/i);
  if (match) {
    return match[1].trim();
  }
  return 'Usuario';
};

/**
 * Determine if a role tag should have standard tasks
 */
const roleHasStandardTasks = (title: string, rol: string): boolean => {
  const combined = `${title} ${rol}`.toLowerCase();
  return combined.includes('[back]') || 
         combined.includes('[front]') || 
         combined.includes('[integr') ||
         combined.includes('backend') ||
         combined.includes('frontend') ||
         combined.includes('integración') ||
         combined.includes('desarrollador');
};

/**
 * Parse acceptance criteria from text block
 */
const parseAcceptanceCriteria = (text: string): string[] => {
  const criteria: string[] = [];
  
  // Handle <br> tags and bullet points
  const cleanedText = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/•/g, '\n•')
    .replace(/\\n/g, '\n');
  
  const lines = cleanedText.split(/[\n\r]+/);
  
  for (const line of lines) {
    let trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) continue;
    
    // Clean up criterion
    trimmed = trimmed
      .replace(/^[\d]+\.\s*/, '')
      .replace(/^[-*•]\s*/, '')
      .replace(/^Cr\d+:\s*/i, '')
      .trim();
    
    if (trimmed.length > 5) {
      criteria.push(trimmed);
    }
  }
  
  return criteria;
};

const parseSegmentedCriteria = (
  functionalRaw: string,
  technicalRaw: string,
  title: string,
  rol: string
): { funcionales: string[]; tecnicos: string[] } => {
  const funcionales = parseAcceptanceCriteria(functionalRaw || '');
  let tecnicos = parseAcceptanceCriteria(technicalRaw || '');

  if (tecnicos.length === 0) {
    tecnicos = getDefaultTechnicalCriteriaForStory(title, rol);
  }

  return { funcionales, tecnicos };
};

const parseStatus = (text: string): UserStory['status'] => {
  const lower = text.toLowerCase();
  if (lower.includes('revisión') || lower.includes('revision') || lower.includes('review')) {
    return 'review';
  }
  if (lower.includes('aprobada') || lower.includes('aprobado') || lower.includes('approved')) {
    return 'approved';
  }
  return 'draft';
};

const stripInlineMarkdown = (text: string): string => {
  return text
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/<br\s*\/?>/gi, ' ')
    .trim();
};

const parseTaskTypeFromTitle = (title: string): StandardTask['type'] => {
  const lower = title.toLowerCase();
  if (lower.includes('context')) return 'Contextualización';
  if (lower.includes('prueb')) return 'Pruebas';
  if (lower.includes('desplieg') || lower.includes('producción') || lower.includes('produccion')) {
    return 'Despliegue';
  }
  return 'Desarrollo';
};

const parseTaskLine = (line: string): StandardTask | null => {
  const cleanLine = stripInlineMarkdown(line);
  if (!cleanLine || cleanLine === '-') return null;

  let candidate = cleanLine
    .replace(/^[-*•]\s*/, '')
    .replace(/^\[[xX\s]\]\s*/, '')
    .replace(/^\d+[.)]\s*/, '')
    .trim();

  if (!candidate) return null;

  let explicitDescription = '';
  const descriptionMatch = candidate.match(/^(.*?)\s*::\s*(.+)$/);
  if (descriptionMatch) {
    candidate = descriptionMatch[1].trim();
    explicitDescription = descriptionMatch[2].trim();
  }

  let estimatedHours = 1;
  const hourMatch = candidate.match(/(.+?)\s*\((\d+(?:[.,]\d+)?)\s*h(?:oras?)?\)\s*$/i);
  if (hourMatch) {
    candidate = hourMatch[1].trim();
    estimatedHours = Math.max(1, Math.round(parseFloat(hourMatch[2].replace(',', '.')) || 1));
  }

  const template = STANDARD_TASKS.find(task => {
    const taskTitle = task.title.toLowerCase();
    const candidateLower = candidate.toLowerCase();
    return taskTitle === candidateLower || taskTitle.includes(candidateLower) || candidateLower.includes(taskTitle);
  });

  return {
    title: candidate,
    description: explicitDescription || template?.description || '',
    type: template?.type || parseTaskTypeFromTitle(candidate),
    estimated_hours: estimatedHours
  };
};

const parseTasksFromText = (text: string): StandardTask[] => {
  if (!text) return [];

  const normalized = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\n/g, '\n');

  const tasks = normalized
    .split(/[\n\r]+/)
    .map(line => parseTaskLine(line))
    .filter((task): task is StandardTask => Boolean(task));

  const deduped: StandardTask[] = [];
  const seen = new Set<string>();
  for (const task of tasks) {
    const key = `${task.title.toLowerCase()}::${task.estimated_hours}`;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(task);
    }
  }

  return deduped;
};

const parseListItems = (text: string): string[] => {
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\n/g, '\n')
    .split(/[\n\r]+/)
    .map(line => stripInlineMarkdown(line).replace(/^[-*•]\s*/, '').replace(/^\d+[.)]\s*/, '').trim())
    .filter(Boolean)
    .filter(line => line !== '-' && !/^sin\s+/i.test(line));
};

const extractLinks = (text: string): string[] => {
  const links: string[] = [];
  const matches = text.matchAll(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)|https?:\/\/[^\s<>")\]]+/gi);
  for (const match of matches) {
    links.push((match[2] || match[0]).trim());
  }
  return Array.from(new Set(links));
};

/**
 * Parse insumos from text
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const parseInsumos = (text: string): string[] => {
  const cleanedText = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/•/g, '\n•')
    .replace(/\\n/g, '\n');
  
  return cleanedText
    .split(/[\n\r]+/)
    .map(i => i.replace(/^[-*•]\s*/, '').trim())
    .filter(i => i.length > 2 && i !== '-');
};

/**
 * Parse Tareas Estándar from the agent output
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const parseTareasEstandar = (text: string): string[] => {
  if (!text || text === '-') return [];
  
  const cleanedText = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\n/g, '\n');
  
  return cleanedText
    .split(/[\n\r]+/)
    .map(t => t.replace(/^\d+\.\s*/, '').trim())
    .filter(t => t.length > 3);
};

/**
 * Parse stories from markdown table format
 * Handles tables with columns: Title | UserStory | Rol | CriteriosFuncionales | FigmaLinks | Insumos | StoryPoints | Épica | Prioridad
 * ONLY parses User Story tables, skips Epic tables and summary sections
 * Supports trio format: [Back], [Front], [Integración]
 */
const parseStoriesFromTable = (markdown: string): UserStory[] => {
  const stories: UserStory[] = [];
  
  // Find markdown tables (lines starting with |)
  const tableRegex = /\|(.+)\|[\r\n]+\|[-:\s|]+\|[\r\n]+((?:\|.+\|[\r\n]*)+)/g;
  let tableMatch;
  
  while ((tableMatch = tableRegex.exec(markdown)) !== null) {
    const headerLine = tableMatch[1];
    const dataLines = tableMatch[2];
    
    // Parse header columns - more flexible matching
    const headers = headerLine.split('|').map(h => h.trim().toLowerCase().replace(/\*\*/g, ''));
    
    // Check if this is a User Story table - must have UserStory/Title + criteria indicators
    const hasUserStoryColumn = headers.some(h => 
      h.includes('userstory') || h.includes('user_story') || h.includes('yo como') || h.includes('historia')
    );
    const hasTitleColumn = headers.some(h => h.includes('title') || h.includes('título'));
    const hasCriteriaColumn = headers.some(h => h.includes('criterio') || h.includes('aceptación') || h.includes('funcional'));
    const hasStoryPointsColumn = headers.some(h => h.includes('point') || h.includes('punto') || h.includes('sp'));
    
    // SKIP if this is an epics-only table (has épica but NO userstory/criteria/storypoints)
    // Epic tables typically have: Épica | Descripción | Valor (3 columns max)
    const isEpicOnlyTable = headers.some(h => h === 'épica' || h === 'epic') &&
                            !hasUserStoryColumn && 
                            !hasCriteriaColumn &&
                            !hasStoryPointsColumn &&
                            headers.length <= 4;
    
    if (isEpicOnlyTable) {
      console.log('⏭️ Skipping epic-only table:', headers);
      continue;
    }
    
    // SKIP if this looks like a summary/campo-valor table (Campo | Valor pattern)
    const isSummaryTable = headers.some(h => h === 'campo' || h === 'field') && 
                           headers.some(h => h === 'valor' || h === 'value');
    if (isSummaryTable) {
      console.log('⏭️ Skipping summary table:', headers);
      continue;
    }
    
    // Check if this is a valid User Story table
    const isValidStoryTable = hasUserStoryColumn || (hasTitleColumn && hasCriteriaColumn);
    
    if (!isValidStoryTable) {
      console.log('⏭️ Skipping non-story table:', headers);
      continue;
    }
    
    console.log('✅ Processing story table:', headers);
    
    // Map column indices with flexible matching
    const columnMap: Record<string, number> = {};
    headers.forEach((header, index) => {
      const h = header.toLowerCase().trim();
      if ((h === 'id' || h.startsWith('id')) && !columnMap['id']) {
        columnMap['id'] = index;
      }
      // Title column - be more specific to avoid false positives
      if ((h === 'title' || h === 'título' || h.startsWith('title') || h.startsWith('título')) && !columnMap['title']) {
        columnMap['title'] = index;
      }
      if (h.includes('userstory') || h.includes('user_story') || h.includes('historia') || h === 'descripción' || h.includes('yo como')) {
        columnMap['userStory'] = index;
      }
      if (h === 'rol' || h === 'role' || h.includes('actor')) {
        columnMap['rol'] = index;
      }
      if ((h.includes('criterio') && h.includes('tecnic')) || h.includes('criteriostecnicos')) {
        columnMap['criteriosTecnicos'] = index;
      } else if (h.includes('criteriosfuncionales') || h.includes('funcional')) {
        columnMap['criteriosFuncionales'] = index;
      } else if (h.includes('criterio') || h.includes('aceptación') || h.includes('acceptance')) {
        columnMap['criterios'] = index;
      }
      if (h.includes('figma') || h.includes('diseño') || h.includes('mockup')) {
        columnMap['figma'] = index;
      }
      if (h.includes('insumo') || h.includes('recurso') || h.includes('documento') || h.includes('input')) {
        columnMap['insumos'] = index;
      }
      if (h.includes('point') || h.includes('punto') || h === 'sp' || h.includes('estimación')) {
        columnMap['storyPoints'] = index;
      }
      if (h.includes('priorit') || h.includes('prioridad')) {
        columnMap['priority'] = index;
      }
      if (h.includes('estado') || h.includes('status')) {
        columnMap['status'] = index;
      }
      if (h === 'epic' || h === 'épica' || h.includes('épica')) {
        columnMap['epic'] = index;
      }
      if (h.includes('tarea') || h.includes('task')) {
        columnMap['tasks'] = index;
      }
    });
    
    console.log('Column mapping:', columnMap, 'Headers:', headers);
    
    // Parse data rows
    const rows = dataLines.trim().split(/[\r\n]+/).filter(r => r.includes('|'));
    
    for (const row of rows) {
      // Split by | but keep content inside
      const cells = row.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);
      
      if (cells.length < 2) continue;
      
      // Extract title
      let title = cells[columnMap['title']] || cells[0] || '';
      // Clean markdown formatting from title
      title = title.replace(/\*\*/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').trim();
      const parsedStoryId = resolveStoryId(cells[columnMap['id']] || '', stories.length);
      
      // Extract user story - IMPORTANT: clean HTML entities and formatting
      let userStory = cells[columnMap['userStory']] || cells[1] || '';
      userStory = userStory
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/\*\*/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\\n/g, ' ')
        .trim();
      
      // Extract rol - if not in column, try to extract from title or user story
      let rol = cells[columnMap['rol']] || '';
      rol = rol.replace(/<br\s*\/?>/gi, ' ').replace(/\*\*/g, '').trim();
      if (!rol) {
        rol = extractRole(userStory, title);
      }
      
      // Extract segmented acceptance criteria
      const criteriosFuncionalesRaw = (
        cells[columnMap['criteriosFuncionales']] ||
        cells[columnMap['criterios']] ||
        ''
      )
        .replace(/^["']|["']$/g, '')
        .replace(/\\n/g, '\n');

      const criteriosTecnicosRaw = (
        cells[columnMap['criteriosTecnicos']] || ''
      )
        .replace(/^["']|["']$/g, '')
        .replace(/\\n/g, '\n');

      const segmentedCriteria = parseSegmentedCriteria(
        criteriosFuncionalesRaw,
        criteriosTecnicosRaw,
        title,
        rol
      );
      
      // Extract Figma links
      let figmaRaw = cells[columnMap['figma']] || '';
      const figmaLinks: string[] = [];
      // Match URLs and markdown links
      const figmaMatches = figmaRaw.matchAll(/https?:\/\/[^\s<>")\]]+|\[([^\]]+)\]\(([^)]+)\)/gi);
      for (const match of figmaMatches) {
        figmaLinks.push(match[2] || match[0]);
      }
      
      // Extract insumos/recursos
      let insumosRaw = cells[columnMap['insumos']] || '';
      insumosRaw = insumosRaw
        .replace(/^["']|["']$/g, '')
        .replace(/\\n/g, '\n');
      
      const insumos = insumosRaw
        .split(/<br\s*\/?>|\n|;/gi)
        .map(i => i.trim())
        .filter(i => i.length > 2);
      
      // Extract story points
      const storyPointsRaw = cells[columnMap['storyPoints']] || '3';
      const storyPoints = parseStoryPoints(storyPointsRaw);
      
      // Extract priority
      const priorityRaw = cells[columnMap['priority']] || 'Media';
      const priorityData = parsePriorityAndSequence(priorityRaw);
      
      // Extract epic - can be an ID (number) or a name
      let epic = cells[columnMap['epic']] || extractEpicFromTitle(title);
      // Clean the epic value
      epic = epic.replace(/\*\*/g, '').replace(/<br\s*\/?>/gi, '').trim();
      // If it's empty or just dashes, try to extract from title
      if (!epic || epic === '-' || epic === 'N/A') {
        epic = extractEpicFromTitle(title);
      }
      
      // Determine if this role should have standard tasks
      // Only Back, Front, and Integración have standard tasks defined
      const shouldHaveTasks = roleHasStandardTasks(title, rol);
      
      // Check if tasks column exists and has content (not just "-")
      const tasksRaw = cells[columnMap['tasks']] || '';
      const parsedTasks = parseTasksFromText(tasksRaw);
      const hasTareasFromAgent = parsedTasks.length > 0;

      // Extract status (optional)
      const statusRaw = cells[columnMap['status']] || 'draft';
      const status = parseStatus(statusRaw);
      
      // Only add if we have meaningful data
      if (title || userStory) {
        stories.push({
          id: parsedStoryId,
          title: title || `Historia ${stories.length + 1}`,
          user_story: userStory,
          rol,
          criterios_funcionales: segmentedCriteria.funcionales,
          criterios_tecnicos: segmentedCriteria.tecnicos,
          figma_links: figmaLinks,
          insumos,
          story_points: storyPoints,
          // Keep explicit tasks from source; fallback to defaults only when source has no tasks at all
          tasks: hasTareasFromAgent ? parsedTasks : (shouldHaveTasks ? [...STANDARD_TASKS] : []),
          priority: priorityData.priority,
          priority_sequence: priorityData.priority_sequence,
          epic: epic || undefined,
          status
        });
      }
    }
  }
  
  return stories;
};

/**
 * Extract epic code from title pattern like TP01-E1-ARCH-...
 */
const extractEpicFromTitle = (title: string): string => {
  const match = title.match(/-(E\d+)-/i);
  return match ? match[1] : '';
};

/**
 * Parse a single user story section from markdown
 * Validates that the section is actually a User Story, not an Epic or Summary
 */
const parseUserStorySection = (section: string, index: number): UserStory | null => {
  try {
    const sectionLower = section.toLowerCase();
    
    // SKIP sections that are clearly NOT user stories
    // Skip epic sections
    if (sectionLower.includes('## épica') || sectionLower.includes('## epic') || 
        sectionLower.includes('### épica') || sectionLower.match(/^épica\s*\d/im)) {
      console.log('⏭️ Skipping epic section');
      return null;
    }
    
    // Skip summary/executive sections
    if (sectionLower.includes('resumen ejecutivo') || sectionLower.includes('executive summary') ||
        sectionLower.includes('## resumen') || sectionLower.includes('## 1.')) {
      console.log('⏭️ Skipping summary section');
      return null;
    }
    
    // Skip table-only sections (tables are parsed separately)
    if (section.trim().startsWith('|') && !section.includes('como')) {
      console.log('⏭️ Skipping table-only section');
      return null;
    }

    let storyId = '';
    let title = '';
    let userStory = '';
    let rol = '';
    let epic: string | undefined;
    let priority: 'Alta' | 'Media' | 'Baja' = 'Media';
    let prioritySequence: number | null = null;
    let storyPoints = 3;
    let status: UserStory['status'] = 'draft';

    let activeBlock: 'none' | 'description' | 'funcional' | 'tecnico' | 'tasks' | 'figma' | 'insumos' = 'none';
    const descriptionLines: string[] = [];
    const functionalLines: string[] = [];
    const technicalLines: string[] = [];
    const taskLines: string[] = [];
    const figmaLines: string[] = [];
    const insumoLines: string[] = [];

    const lines = section.split(/[\r\n]+/);

    // Ignore non-story blocks (stats, section headers, summaries, etc.)
    if (!lines.some((line) => STORY_HEADER_LINE_REGEX.test(line))) {
      return null;
    }

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line || /^═+$/.test(line)) {
        continue;
      }

      if (!title) {
        const titleMatch = line.match(STORY_HEADER_CAPTURE_REGEX);
        if (titleMatch) {
          storyId = resolveStoryId(titleMatch[1], index);
          title = stripInlineMarkdown((titleMatch[2] || '').replace(/^:\s*/, ''));
          continue;
        }
      }

      const normalizedLine = line.replace(/\*\*/g, '').trim();
      let fieldMatch: RegExpMatchArray | null = null;

      if ((fieldMatch = normalizedLine.match(/^[eé]pica\s*:\s*(.+)$/i))) {
        epic = stripInlineMarkdown(fieldMatch[1]) || undefined;
        activeBlock = 'none';
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^prioridad\s*:\s*(.+)$/i))) {
        const parsedPriority = parsePriorityAndSequence(fieldMatch[1]);
        priority = parsedPriority.priority;
        prioritySequence = parsedPriority.priority_sequence;
        activeBlock = 'none';
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^(?:tag\s*prioridad|secuencia\s*prioridad|prioridad\s*secuencia)\s*:\s*(.+)$/i))) {
        prioritySequence = normalizePrioritySequence(fieldMatch[1]);
        activeBlock = 'none';
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^(?:story\s*points?|puntos?|estimaci[oó]n|sp)\s*:\s*(.+)$/i))) {
        storyPoints = parseStoryPoints(fieldMatch[1]);
        activeBlock = 'none';
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^rol\s*:\s*(.+)$/i))) {
        rol = stripInlineMarkdown(fieldMatch[1]);
        activeBlock = 'none';
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^estado\s*:\s*(.+)$/i))) {
        status = parseStatus(fieldMatch[1]);
        activeBlock = 'none';
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^descripci[oó]n\s*:\s*(.*)$/i))) {
        activeBlock = 'description';
        if (fieldMatch[1]) {
          descriptionLines.push(fieldMatch[1]);
        }
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^criterios?\s*funcionales?\s*:\s*(.*)$/i))) {
        activeBlock = 'funcional';
        if (fieldMatch[1]) {
          functionalLines.push(fieldMatch[1]);
        }
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^criterios?\s*t[eé]cnicos?\s*:\s*(.*)$/i))) {
        activeBlock = 'tecnico';
        if (fieldMatch[1]) {
          technicalLines.push(fieldMatch[1]);
        }
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^criterios?\s*(?:de\s*)?aceptaci[oó]n\s*:\s*(.*)$/i))) {
        activeBlock = 'funcional';
        if (fieldMatch[1]) {
          functionalLines.push(fieldMatch[1]);
        }
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^tareas?\s*:\s*(.*)$/i))) {
        activeBlock = 'tasks';
        if (fieldMatch[1]) {
          taskLines.push(fieldMatch[1]);
        }
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^(?:enlaces?\s+figma|figma)\s*:\s*(.*)$/i))) {
        activeBlock = 'figma';
        if (fieldMatch[1]) {
          figmaLines.push(fieldMatch[1]);
        }
        continue;
      }
      if ((fieldMatch = normalizedLine.match(/^(?:insumos?|recursos?)\s*:\s*(.*)$/i))) {
        activeBlock = 'insumos';
        if (fieldMatch[1]) {
          insumoLines.push(fieldMatch[1]);
        }
        continue;
      }

      const cleanLine = stripInlineMarkdown(line);
      if (!userStory && /como\s+.+quiero\s+.+para\s+/i.test(cleanLine)) {
        userStory = cleanLine;
      }

      if (activeBlock === 'description') {
        if (cleanLine && !/^sin\s+descrip/i.test(cleanLine)) {
          descriptionLines.push(cleanLine);
        }
      } else if (activeBlock === 'funcional') {
        functionalLines.push(line);
      } else if (activeBlock === 'tecnico') {
        technicalLines.push(line);
      } else if (activeBlock === 'tasks') {
        taskLines.push(line);
      } else if (activeBlock === 'figma') {
        figmaLines.push(line);
      } else if (activeBlock === 'insumos') {
        insumoLines.push(line);
      }
    }

    if (!title) {
      const titleMatch = section.match(/^\s*(?:#{2,3}\s*)?(?:\*\*)?HU-\d+(?:\*\*)?\s*[:-]?\s*([^\n]*)/im);
      if (titleMatch) {
        title = stripInlineMarkdown(titleMatch[1].replace(/^[:\s]+/, ''));
      }
    }

    if (!storyId) {
      const sectionIdMatch = section
        .split(/[\r\n]+/)
        .slice(0, 8)
        .join('\n')
        .match(/\b(HU-\d+)\b/i);
      storyId = resolveStoryId(sectionIdMatch?.[1], index);
    }

    if (!userStory) {
      const fromDescription = stripInlineMarkdown(descriptionLines.join(' ').trim());
      if (fromDescription && !/^sin\s+descrip/i.test(fromDescription)) {
        userStory = fromDescription;
      } else {
        const storyMatch = section.match(/como\s+.+quiero\s+.+para\s+[^\n]+/i);
        if (storyMatch) {
          userStory = stripInlineMarkdown(storyMatch[0]);
        }
      }
    }

    if (!rol) {
      rol = extractRole(userStory, title);
    }

    if (!epic) {
      const epicMatch = section.match(/[eé]pica[:\s]*([^\n]+)/i);
      if (epicMatch) {
        epic = stripInlineMarkdown(epicMatch[1]) || undefined;
      }
    }

    if (priority === 'Media') {
      const priorityMatch = section.match(/prioridad[:\s]*([^\n]+)/i);
      if (priorityMatch) {
        const parsedPriority = parsePriorityAndSequence(priorityMatch[1]);
        priority = parsedPriority.priority;
        if (prioritySequence === null) {
          prioritySequence = parsedPriority.priority_sequence;
        }
      }
    }

    if (prioritySequence === null) {
      const priorityTagMatch = section.match(/#P(\d{1,6})/i);
      if (priorityTagMatch) {
        prioritySequence = normalizePrioritySequence(priorityTagMatch[1]);
      }
    }

    if (storyPoints === 3) {
      const pointsMatch = section.match(/(?:estimaci[oó]n|puntos?|points?|sp)[:\s]*(\d+|[smlx]+)/i);
      if (pointsMatch) {
        storyPoints = parseStoryPoints(pointsMatch[1]);
      }
    }

    const structuredEditorFormat = /\*\*descripci[oó]n:\*\*|\*\*estado:\*\*|\*\*prioridad:\*\*/i.test(section);

    if (functionalLines.length === 0 && technicalLines.length === 0) {
      const functionalSection = section.match(
        /criterios?\s*funcionales?[:\s]*\n([\s\S]+?)(?=\n\*{0,2}\s*criterios?\s*t[eé]cnicos?|\n(?:notas?|dependencias?|#{2,}|$))/i
      );
      const technicalSection = section.match(
        /criterios?\s*t[eé]cnicos?[:\s]*\n([\s\S]+?)(?=\n(?:notas?|dependencias?|#{2,}|$))/i
      );
      const genericCriteriaSection = section.match(
        /criterios?\s*(?:de\s*)?aceptaci[oó]n[:\s]*\n([\s\S]+?)(?=\n(?:notas?|dependencias?|#{2,}|$))/i
      );

      if (functionalSection?.[1] || genericCriteriaSection?.[1]) {
        functionalLines.push(functionalSection?.[1] || genericCriteriaSection?.[1] || '');
      }
      if (technicalSection?.[1]) {
        technicalLines.push(technicalSection[1]);
      }
    }

    const criteriosFuncionales = parseAcceptanceCriteria(functionalLines.join('\n'));
    let criteriosTecnicos = parseAcceptanceCriteria(technicalLines.join('\n'));
    if (criteriosTecnicos.length === 0 && !structuredEditorFormat) {
      criteriosTecnicos = getDefaultTechnicalCriteriaForStory(title, rol);
    }

    let tasks = parseTasksFromText(taskLines.join('\n'));
    if (tasks.length === 0 && !structuredEditorFormat) {
      const shouldHaveTasks = roleHasStandardTasks(title, rol);
      if (shouldHaveTasks) {
        tasks = [...STANDARD_TASKS];
      }
    }

    const figmaBlock = figmaLines.join('\n');
    let figmaLinks = extractLinks(figmaBlock);
    if (figmaLinks.length === 0 && figmaBlock.trim()) {
      figmaLinks = parseListItems(figmaBlock);
    }
    if (figmaLinks.length === 0) {
      const figmaMatches = section.matchAll(/https?:\/\/(?:www\.)?figma\.com\/[^\s)>\]]+/gi);
      for (const match of figmaMatches) {
        figmaLinks.push(match[0]);
      }
      figmaLinks = Array.from(new Set(figmaLinks));
    }

    const insumos = parseListItems(insumoLines.join('\n'));

    // Only return if we have at least a title or description
    if (!title && !userStory) {
      return null;
    }

    return {
      id: storyId,
      title: title || `Historia ${index + 1}`,
      user_story: userStory,
      rol,
      criterios_funcionales: criteriosFuncionales,
      criterios_tecnicos: criteriosTecnicos,
      figma_links: figmaLinks,
      insumos,
      story_points: storyPoints,
      tasks,
      priority,
      priority_sequence: prioritySequence,
      epic,
      status
    };
  } catch (error) {
    console.error('Error parsing story section:', error);
    return null;
  }
};

/**
 * Parse epics from markdown table format
 * Handles tables with columns: Épica | Descripción | Valor de Negocio
 */
const parseEpicsFromTable = (markdown: string): Epic[] => {
  const epics: Epic[] = [];
  
  // Find epic tables (usually have "épica" or "epic" in header)
  const tableRegex = /\|(.+[eé]pica.+)\|[\r\n]+\|[-:\s|]+\|[\r\n]+((?:\|.+\|[\r\n]*)+)/gi;
  let tableMatch;
  
  while ((tableMatch = tableRegex.exec(markdown)) !== null) {
    const headerLine = tableMatch[1].toLowerCase();
    const dataLines = tableMatch[2];
    
    // Only parse if this is an epics table (not stories)
    if (headerLine.includes('userstory') || headerLine.includes('storypoints')) {
      continue; // Skip, this is a stories table
    }
    
    // Parse header columns
    const headers = headerLine.split('|').map(h => h.trim());
    const columnMap: Record<string, number> = {};
    headers.forEach((header, index) => {
      if (header.includes('épica') || header.includes('epic')) columnMap['title'] = index;
      else if (header.includes('descripci') || header.includes('description')) columnMap['description'] = index;
      else if (header.includes('valor') || header.includes('value')) columnMap['value'] = index;
    });
    
    // Parse data rows
    const rows = dataLines.trim().split(/[\r\n]+/).filter(r => r.includes('|'));
    
    for (const row of rows) {
      const cells = row.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);
      
      if (cells.length < 2) continue;
      
      let title = cells[columnMap['title']] || cells[0] || '';
      title = title.replace(/\*\*/g, '').trim();
      
      const description = cells[columnMap['description']] || cells[1] || '';
      const value = cells[columnMap['value']] || cells[2] || '';
      
      if (title) {
        epics.push({
          id: generateId(),
          title,
          description: `${description}${value ? ` | Valor: ${value}` : ''}`.trim(),
          stories: []
        });
      }
    }
  }
  
  return epics;
};

/**
 * Parse epics from markdown
 */
const parseEpics = (markdown: string): Epic[] => {
  const epics: Epic[] = [];
  
  // Look for epic patterns
  const epicPattern = /[eé]pica[:\s]*([^\n]+)\n(?:descripci[oó]n[:\s]*)?([^\n]*)/gi;
  let match;
  
  while ((match = epicPattern.exec(markdown)) !== null) {
    epics.push({
      id: generateId(),
      title: match[1].trim(),
      description: match[2]?.trim() || '',
      stories: []
    });
  }
  
  return epics;
};

/**
 * Extract executive summary
 */
const extractSummary = (markdown: string): string => {
  // Look for summary section
  const summaryMatch = markdown.match(/(?:resumen\s*ejecutivo|executive\s*summary|resumen)[:\s]*\n([\s\S]+?)(?=\n#{2,}|épica|$)/i);
  if (summaryMatch) {
    return summaryMatch[1].trim().substring(0, 1000);
  }
  
  // Take first paragraph if no explicit summary
  const firstParagraph = markdown.match(/^(?!#)(.+(?:\n(?!#).+)*)/m);
  if (firstParagraph) {
    return firstParagraph[1].trim().substring(0, 500);
  }
  
  return '';
};

/**
 * Extract textual business rules section from markdown
 */
const extractBusinessRules = (markdown: string): string[] => {
  const sectionMatch = markdown.match(
    /#{2,3}\s*reglas?\s+de\s+negocio[^\n]*\n([\s\S]+?)(?=\n#{1,6}\s+|$)/i
  );

  if (!sectionMatch) {
    return [];
  }

  const candidates = sectionMatch[1]
    .replace(/<br\s*\/?>/gi, '\n')
    .split(/[\n\r]+/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => line.replace(/^[-*•]\s*/, '').replace(/^\d+[.)]\s*/, '').trim())
    .filter(line => line.length > 8)
    .filter(line => !line.toLowerCase().startsWith('estas reglas'));

  const deduped: string[] = [];
  const seen = new Set<string>();
  for (const rule of candidates) {
    const normalized = rule.replace(/\s+/g, ' ').trim();
    if (!seen.has(normalized.toLowerCase())) {
      seen.add(normalized.toLowerCase());
      deduped.push(normalized);
    }
  }

  return deduped;
};

/**
 * Main parser function - converts markdown backlog to structured data
 */
export const parseBacklogMarkdown = (markdown: string): ParsedBacklog => {
  // Extract executive summary
  const executive_summary = extractSummary(markdown);
  const business_rules = extractBusinessRules(markdown);
  
  // Parse epics - try table format first, then fallback
  const epics = parseEpicsFromTable(markdown) || parseEpics(markdown);
  
  // Try parsing stories from table format FIRST (new format)
  let stories: UserStory[] = parseStoriesFromTable(markdown);
  
  // If no stories from tables, try section-based parsing
  if (stories.length === 0) {
    // Split markdown into story sections - look for HU patterns specifically
    // Avoid matching generic "Historias de Usuario" headings that are not real HUs
    const storyDelimiter = /(?=^\s*#{2,3}\s*(?:\*\*)?HU-\d+(?:\*\*)?\s*(?:[:-]|$)|^\s*(?:\*\*)?HU-\d+(?:\*\*)?\s*(?:[:-]|$))/gim;
    const sections = markdown.split(storyDelimiter)
      .filter(s => s.trim().length > 50)
      .filter(sectionHasStoryHeader)
      // Filter out sections that are clearly epics or summaries
      .filter(s => {
        const lower = s.toLowerCase();
        return !lower.includes('## épica') && 
               !lower.includes('resumen ejecutivo') &&
               !lower.includes('## 1.') &&
               !lower.includes('## 2. épicas');
      });
    
    // Parse each section
    sections.forEach((section, index) => {
      const story = parseUserStorySection(section, index);
      if (story) {
        stories.push(story);
      }
    });
  }
  
  // If still no stories found, try alternative parsing
  if (stories.length === 0) {
    // Look for "Como ... quiero ... para" patterns
    const storyMatches = markdown.matchAll(/(?:^|\n)(.*como\s+.+quiero\s+.+para\s+[^\n]+)/gi);
    let idx = 0;
    for (const match of storyMatches) {
      const storyText = match[1].trim();
      const rol = extractRole(storyText);
      const shouldHaveTasks = roleHasStandardTasks('', rol);
      
      const story: UserStory = {
        id: buildHuId(idx + 1),
        title: `Historia ${idx + 1}`,
        user_story: storyText,
        rol,
        criterios_funcionales: [],
        criterios_tecnicos: getDefaultTechnicalCriteriaForStory('', rol),
        figma_links: [],
        insumos: [],
        story_points: 3,
        tasks: shouldHaveTasks ? [...STANDARD_TASKS] : [],
        priority: 'Media',
        priority_sequence: null,
        status: 'draft'
      };
      stories.push(story);
      idx++;
    }
  }
  
  // Link stories to epics if possible
  stories.forEach(story => {
    if (story.epic) {
      const matchingEpic = epics.find(e => 
        e.title.toLowerCase().includes(story.epic!.toLowerCase()) ||
        story.epic!.toLowerCase().includes(e.title.toLowerCase())
      );
      if (matchingEpic) {
        matchingEpic.stories.push(story);
      }
    }
  });
  
  return {
    executive_summary,
    business_rules,
    epics,
    stories,
    raw_markdown: markdown
  };
};

/**
 * Convert structured backlog back to markdown
 */
export const backlogToMarkdown = (backlog: ParsedBacklog): string => {
  const lines: string[] = [];
  
  // Header
  lines.push('# Backlog Generado');
  lines.push('');
  lines.push(`*Generado el ${new Date().toLocaleDateString('es-CO')}*`);
  lines.push('');
  
  // Executive Summary
  if (backlog.executive_summary) {
    lines.push('## Resumen Ejecutivo');
    lines.push('');
    lines.push(backlog.executive_summary);
    lines.push('');
  }

  if (backlog.business_rules && backlog.business_rules.length > 0) {
    lines.push('## Reglas de negocio identificadas');
    lines.push('');
    backlog.business_rules.forEach(rule => {
      lines.push(`- ${rule}`);
    });
    lines.push('');
  }
  
  // Statistics
  const totalPoints = backlog.stories.reduce((sum, s) => sum + s.story_points, 0);
  lines.push('## Estadísticas');
  lines.push('');
  lines.push(`- **Total de Historias:** ${backlog.stories.length}`);
  lines.push(`- **Story Points Totales:** ${totalPoints}`);
  lines.push(`- **Épicas:** ${backlog.epics.length}`);
  lines.push('');
  
  // Epics
  if (backlog.epics.length > 0) {
    lines.push('## Épicas');
    lines.push('');
    backlog.epics.forEach((epic, idx) => {
      lines.push(`### ${idx + 1}. ${epic.title}`);
      if (epic.description) {
        lines.push(epic.description);
      }
      lines.push('');
    });
  }
  
  // User Stories
  lines.push('## 📌 HISTORIAS DE USUARIO - SEGMENTADAS POR DOMINIO');
  lines.push('');
  
  backlog.stories.forEach((story, idx) => {
    const storyId = resolveStoryId(story.id, idx);
    lines.push('═'.repeat(60));
    lines.push(`### ${storyId}: ${story.title}`);
    lines.push('═'.repeat(60));
    lines.push('');
    
    if (story.epic) {
      lines.push(`**Épica:** ${story.epic}`);
    }
    lines.push(`**Prioridad:** ${story.priority}${story.priority_sequence ? ` (Secuencia ${story.priority_sequence})` : ''}`);
    const priorityTag = formatPriorityTag(story.priority_sequence);
    if (priorityTag) {
      lines.push(`**Tag Prioridad:** ${priorityTag}`);
    }
    lines.push(`**Story Points:** ${story.story_points}`);
    lines.push(`**Rol:** ${story.rol}`);
    lines.push(`**Estado:** ${story.status === 'draft' ? 'Borrador' : story.status === 'review' ? 'En Revisión' : 'Aprobada'}`);
    lines.push('');
    
    lines.push('**Descripción:**');
    lines.push(story.user_story || '*Sin descripción*');
    lines.push('');
    
    if (story.criterios_funcionales.length > 0) {
      lines.push('**Criterios Funcionales:**');
      story.criterios_funcionales.forEach((c, i) => {
        lines.push(`${i + 1}. ${c}`);
      });
      lines.push('');
    }

    if (story.criterios_tecnicos.length > 0) {
      lines.push('**Criterios Técnicos:**');
      story.criterios_tecnicos.forEach((c, i) => {
        lines.push(`${i + 1}. ${c}`);
      });
      lines.push('');
    }
    
    if (story.tasks.length > 0) {
      lines.push('**Tareas:**');
      story.tasks.forEach(t => {
        const taskTitle = (t.title || '').trim() || 'Tarea';
        const taskHours = Number.isFinite(t.estimated_hours) ? Math.max(1, Math.round(t.estimated_hours)) : 1;
        const taskDescription = (t.description || '').replace(/\s+/g, ' ').trim();
        const serializedDescription = taskDescription ? ` :: ${taskDescription}` : '';
        lines.push(`- [ ] ${taskTitle} (${taskHours}h)${serializedDescription}`);
      });
      lines.push('');
    }
    
    if (story.figma_links.length > 0) {
      lines.push('**Enlaces Figma:**');
      story.figma_links.forEach(link => {
        lines.push(`- ${link}`);
      });
      lines.push('');
    }
    
    if (story.insumos.length > 0) {
      lines.push('**Insumos:**');
      story.insumos.forEach(insumo => {
        lines.push(`- ${insumo}`);
      });
      lines.push('');
    }
    
    lines.push('');
  });
  
  return lines.join('\n');
};

/**
 * Validate that all stories have required fields
 */
export const validateBacklog = (backlog: ParsedBacklog): { valid: boolean; issues: string[] } => {
  const issues: string[] = [];
  
  backlog.stories.forEach((story, idx) => {
    const prefix = resolveStoryId(story.id, idx);
    
    if (!story.title || story.title.length < 5) {
      issues.push(`${prefix}: Falta título válido`);
    }
    
    if (!story.user_story || !/como\s+.+quiero\s+.+para\s+/i.test(story.user_story)) {
      issues.push(`${prefix}: Formato de historia incorrecto (Como/Quiero/Para)`);
    }
    
    if (story.criterios_funcionales.length === 0) {
      issues.push(`${prefix}: Sin criterios de aceptación`);
    }

    if (story.criterios_tecnicos.length === 0 && roleHasStandardTasks(story.title, story.rol)) {
      issues.push(`${prefix}: Sin criterios técnicos para frente de desarrollo`);
    }
    
    // Solo validar tareas para roles de desarrollo (Back, Front, Integración)
    const isDevRole = roleHasStandardTasks(story.title, story.rol);
    if (isDevRole && story.tasks.length < 4) {
      issues.push(`${prefix}: Faltan tareas estándar (requiere 4 para roles de desarrollo)`);
    }
  });
  
  return {
    valid: issues.length === 0,
    issues
  };
};
